// "My Requests" — self-service approvals. Any user can raise a request (discount,
// bid/no-bid, expense, etc.) and track its status. Managers see requests assigned
// to them for approve/reject.
import { useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/page-header";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, Tbody, Td, Th, Thead, Tr } from "@/components/ui/table";
import { BulkModal } from "@/components/bulk-modal";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuthStore } from "@/stores/auth-store";
import { cn } from "@/lib/cn";
import { Plus, Check, X, Inbox, Send } from "lucide-react";

type Tab = "submitted" | "pending";

interface Approval {
  id: string;
  type: "DISCOUNT" | "SUBMISSION" | "BID_NO_BID" | "EXPENSE" | "OTHER";
  status: "PENDING" | "APPROVED" | "REJECTED" | "WITHDRAWN";
  requesterId: string;
  approverId?: string | null;
  subjectType: string;
  subjectId: string;
  requestPayload?: any;
  rationale?: string | null;
  decidedAt?: string | null;
  createdAt: string;
  requester?: { id: string; name: string; email: string };
  approver?: { id: string; name: string; email: string };
}

export default function MyRequestsPage() {
  const me = useAuthStore((s) => s.me);
  const qc = useQueryClient();
  const [tab, setTab] = useState<Tab>("submitted");
  const [decidingId, setDecidingId] = useState<string | null>(null);
  const [decision, setDecision] = useState<"APPROVED" | "REJECTED">("APPROVED");
  const [rationale, setRationale] = useState("");

  const { data } = useQuery({
    queryKey: ["/approvals"],
    queryFn: async () => (await api.get("/approvals")).data.data as Approval[],
  });

  const submitted = (data ?? []).filter((a) => a.requesterId === me?.id);
  const pending = (data ?? []).filter((a) => a.status === "PENDING" && (!a.approverId || a.approverId === me?.id));
  const rows = tab === "submitted" ? submitted : pending;

  const decideMut = useMutation({
    mutationFn: async () => {
      await api.post(`/approvals/${decidingId}/decide`, { status: decision, rationale });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["/approvals"] });
      setDecidingId(null); setRationale("");
    },
  });

  return (
    <>
      <PageHeader
        title="My Requests"
        description="Submit approval requests and act on those assigned to you."
        actions={<Button asChild><Link to="/my-requests/new"><Plus className="mr-1 h-4 w-4"/>New Request</Link></Button>}
      />

      <div className="p-6 space-y-4">
        <div className="flex items-center gap-2">
          <button
            onClick={() => setTab("submitted")}
            className={cn(
              "flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-sm",
              tab === "submitted" ? "border-primary bg-primary/10 text-primary" : "hover:bg-muted",
            )}
          >
            <Send className="h-3.5 w-3.5" />
            Submitted by me ({submitted.length})
          </button>
          <button
            onClick={() => setTab("pending")}
            className={cn(
              "flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-sm",
              tab === "pending" ? "border-primary bg-primary/10 text-primary" : "hover:bg-muted",
            )}
          >
            <Inbox className="h-3.5 w-3.5" />
            Awaiting my approval ({pending.length})
          </button>
        </div>

        <Card>
          <CardContent className="p-0">
            <Table>
              <Thead>
                <Tr>
                  <Th>Subject</Th>
                  <Th>Type</Th>
                  <Th>Status</Th>
                  <Th>{tab === "submitted" ? "Approver" : "Requester"}</Th>
                  <Th>Submitted</Th>
                  <Th>Decided</Th>
                  <Th className="w-24"></Th>
                </Tr>
              </Thead>
              <Tbody>
                {rows.length === 0 && (
                  <Tr><Td colSpan={7} className="py-12 text-center text-muted-foreground">
                    {tab === "submitted"
                      ? <>No requests submitted yet. <Link to="/my-requests/new" className="text-primary hover:underline">Create one →</Link></>
                      : "Nothing awaiting your approval."}
                  </Td></Tr>
                )}
                {rows.map((a) => (
                  <Tr key={a.id}>
                    <Td className="font-medium">{a.subjectType} · {a.subjectId.slice(0, 8)}</Td>
                    <Td>{a.type.replace(/_/g, " ")}</Td>
                    <Td>
                      {a.status === "PENDING"  && <Badge variant="secondary">Pending</Badge>}
                      {a.status === "APPROVED" && <Badge>Approved</Badge>}
                      {a.status === "REJECTED" && <Badge variant="destructive">Rejected</Badge>}
                      {a.status === "WITHDRAWN" && <Badge variant="outline">Withdrawn</Badge>}
                    </Td>
                    <Td className="text-muted-foreground">
                      {tab === "submitted"
                        ? (a.approver?.name ?? "— Any admin —")
                        : (a.requester?.name ?? "—")}
                    </Td>
                    <Td className="text-sm text-muted-foreground">{new Date(a.createdAt).toLocaleDateString()}</Td>
                    <Td className="text-sm text-muted-foreground">
                      {a.decidedAt ? new Date(a.decidedAt).toLocaleDateString() : "—"}
                    </Td>
                    <Td>
                      {tab === "pending" && a.status === "PENDING" && (
                        <div className="flex gap-1">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => { setDecidingId(a.id); setDecision("APPROVED"); setRationale(""); }}
                          >
                            <Check className="h-3.5 w-3.5"/>
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => { setDecidingId(a.id); setDecision("REJECTED"); setRationale(""); }}
                          >
                            <X className="h-3.5 w-3.5 text-destructive"/>
                          </Button>
                        </div>
                      )}
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </CardContent>
        </Card>

        {/* Rationale + request details if one is selected */}
        {rows.some((a) => a.rationale) && (
          <div className="grid gap-3">
            {rows.filter((a) => a.rationale).map((a) => (
              <Card key={`rat-${a.id}`}>
                <CardContent className="p-4">
                  <div className="text-xs text-muted-foreground">
                    {a.requester?.name} · {a.type.replace(/_/g, " ")} · {new Date(a.createdAt).toLocaleString()}
                  </div>
                  <p className="mt-1 whitespace-pre-wrap text-sm">{a.rationale}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      <BulkModal
        open={!!decidingId}
        onClose={() => setDecidingId(null)}
        title={decision === "APPROVED" ? "Approve request" : "Reject request"}
        confirmLabel={decision === "APPROVED" ? "Approve" : "Reject"}
        confirmDanger={decision === "REJECTED"}
        busy={decideMut.isPending}
        onConfirm={() => decideMut.mutate()}
      >
        <Label htmlFor="rationale">Notes (optional)</Label>
        <Input
          id="rationale"
          value={rationale}
          onChange={(e) => setRationale(e.target.value)}
          placeholder={decision === "APPROVED" ? "Looks good" : "Reason for rejection"}
        />
      </BulkModal>
    </>
  );
}
